'use client';

import { Button } from '@/themes/core/ui/button';
import { Input } from '@/themes/core/ui/input';
import { Facebook, Instagram, Twitter } from 'lucide-react';
import Link from 'next/link';

import { Menu } from '@/types/navigation';
import { SiteData } from '../../types';
import { useThemePath } from '@/themes/core/hooks';

function getAbsoluteUrl(url: string) {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    return `https://${url}`;
}

function ModernFooterItem({ item, getLink, depth = 0 }: { item: any, getLink: (path: string) => string, depth?: number }) {
    return (
        <>
            <li>
                <Link
                    href={getLink(item.url || item.path)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors hover:underline underline-offset-4 py-1 block"
                >
                    {item.title}
                </Link>
            </li>
            {item.children && item.children.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                    {item.children.map((child: any) => (
                        <ModernFooterItem key={child.id || child.title} item={child} getLink={getLink} depth={depth + 1} />
                    ))}
                </ul>
            )}
        </>
    );
}

interface ModernFooterProps {
    site: SiteData;
    customMenus?: Menu[];
    footerSettings: any;
}

export function ModernFooter({ site, customMenus = [], footerSettings }: ModernFooterProps) {
    const { getLink } = useThemePath();
    const siteName = site?.name || 'المتجر';
    const logo = site?.logo_url;
    const socialLinks = site?.social_links;
    const direction = footerSettings?.footer_direction || 'auto';

    const currentYear = new Date().getFullYear();
    const copyrightText = footerSettings?.copyright || `© ${currentYear} ${siteName}. All rights reserved.`;

    // Process Custom Sections
    const selectedMenuIds = footerSettings?.footer_menus || [];
    const sections = selectedMenuIds.map((id: string) => {
        const menu = customMenus.find(m => m.id === id || m.handle === id || (m as any).slug === id);
        return {
            title: menu?.title || "قائمة",
            items: menu?.items || []
        };
    }).filter((s: any) => s.items.length > 0);

    return (
        <footer
            className="bg-slate-50 dark:bg-slate-950 py-12 border-t border-border"
            dir={direction === 'auto' ? undefined : (direction as any)}
        >
            <div className="container mx-auto px-4 md:px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12 mb-12">

                    {/* Brand Column (Spans 2 columns on desktop) */}
                    <div className="lg:col-span-2 flex flex-col gap-6">
                        <Link href="/" className="flex items-center gap-2">
                            {logo ? (
                                <img src={logo} alt={siteName} className="h-10 w-auto" width={160} height={40} />
                            ) : (
                                <div className="h-10 w-10 bg-primary/10 text-primary rounded-lg flex items-center justify-center font-bold text-xl border border-primary/20">
                                    {siteName.charAt(0)}
                                </div>
                            )}
                            <span className="text-xl font-bold tracking-tight">{siteName}</span>
                        </Link>

                        <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
                            {site?.description || "متجرك المفضل للتسوق الإلكتروني. نقدم أفضل المنتجات بجودة عالية وأسعار تنافسية مع خدمة توصيل سريعة وموثوقة."}
                        </p>

                        {footerSettings?.showSocial !== false && (
                            <div className="flex gap-3">
                                {socialLinks?.facebook && <SocialIcon label="Facebook" href={getAbsoluteUrl(socialLinks.facebook)} icon={<Facebook size={18} />} className="hover:bg-[#1877F2] hover:text-white" />}
                                {socialLinks?.instagram && <SocialIcon label="Instagram" href={getAbsoluteUrl(socialLinks.instagram)} icon={<Instagram size={18} />} className="hover:bg-[#E1306C] hover:text-white" />}
                                {socialLinks?.twitter && <SocialIcon label="Twitter" href={getAbsoluteUrl(socialLinks.twitter)} icon={<Twitter size={18} />} className="hover:bg-[#1DA1F2] hover:text-white" />}
                                {socialLinks?.tiktok && (
                                    <SocialIcon
                                        label="TikTok"
                                        href={getAbsoluteUrl(socialLinks.tiktok)}
                                        icon={
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                            </svg>
                                        }
                                        className="hover:text-[#ff0050]"
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Navigation Columns */}
                    <div className="lg:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-8">
                        {sections.map((section: any, idx: number) => (
                            <div key={idx} className="flex flex-col gap-4">
                                <h3 className="font-semibold text-foreground text-sm">{section.title}</h3>
                                <ul className="flex flex-col gap-2.5">
                                    {section.items.map((item: any) => (
                                        <ModernFooterItem key={item.id || item.title} item={item} getLink={getLink} />
                                    ))}
                                </ul>
                            </div>
                        ))}
                        {sections.length === 0 && (
                            <div className="col-span-full text-xs italic opacity-50">
                                قم باختيار قوائم لتظهر هنا في إعدادات الثيم.
                            </div>
                        )}
                    </div>
                </div>

                {/* Newsletter & Bottom */}
                <div className="border-t border-border pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
                    {(footerSettings?.showNewsletter ?? true) && (
                        <div className="flex flex-col gap-2 w-full md:w-auto">
                            <p className="text-sm text-foreground font-medium">اشترك في النشرة البريدية</p>
                            <div className="flex gap-2">
                                <Input
                                    type="email"
                                    placeholder="أدخل بريدك الإلكتروني"
                                    className="h-9 w-full md:w-64 bg-background text-sm"
                                />
                                <Button size="sm" className="h-9 px-4 font-bold">اشتراك</Button>
                            </div>
                        </div>
                    )}

                    <div className="text-xs text-muted-foreground text-center md:text-end flex flex-col gap-2 items-center md:items-end flex-1">
                        <p>{copyrightText}</p>
                        {(footerSettings?.showBranding !== false) && (
                            <div className="flex items-center gap-6 mt-1">
                                <a
                                    className="flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-all font-medium"
                                    href="https://ecommaps.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="text-[10px]">مدعوم من</span>
                                    <img
                                        src="/logo-light.png"
                                        alt="Ecommaps"
                                        className="h-4 w-auto brightness-0 dark:brightness-200 grayscale-0"
                                        width={64}
                                        height={16}
                                    />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}

function SocialIcon({ href, icon, className, label }: { href: string; icon: React.ReactNode; className?: string; label: string }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className={`h-9 w-9 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all ${className || ''}`}
        >
            {icon}
        </a>
    )
}
